import { DocumentNode, gql } from '@apollo/client';
import * as React from 'react';

import { getCookieOptions } from '@/modules/accounts/tokenStorage';

import { CurrentProvider_TenantFragment } from './graphql-types/CurrentProvider_TenantFragment';
import { CurrentProvider_UserFragment } from './graphql-types/CurrentProvider_UserFragment';

const USER_FRAGMENT = gql`
  fragment CurrentProvider_UserFragment on User {
    _id
    firstName
    lastName
    acceptedCodeOfConduct
    acceptedPrivacyPolicy
    acceptedTermsAndConditions
    integrationId
    displayName
    integrationIsMember
    canAccessCommunity
    canAccessCommunityReadOnly
    canAccessSponsorPageCommunity
    canMemberPostOnDiscussionBoard
    canAccessDMPage
    role
    accountStatus
    rosterOrganization {
      _id
      sponsorship {
        currentTier {
          analyticsTierType
        }
      }
      currentUserOrganizationPermission {
        canManageOrganizationSponsorship
        canManageOrganizationProfile
        canManageOrganizationMembership
      }
    }
    onboardingFlowState {
      isSkippedOrCompleted
    }
    analyticsUserType
    email
    analyticsRoleType
    membershipData {
      membershipStatus
    }
    visibleGroupLinks {
      _id
      userGroupRole
      group {
        _id
      }
    }
  }
`;

const TENANT_FRAGMENT = gql`
  fragment CurrentProvider_TenantFragment on Tenant {
    _id
    absoluteUrl
    accountCreationPermission
    acronym
    searchMode
    loginContactMessage
    associationCode
    codeOfConduct
    privacyPolicy
    termsAndConditions
    communityAccessConfig
    demo
    displayName
    directMessagesEnabled
    isFullyAddressable
    maintenanceModeOn
    obnoxiousCodeOfConduct
    obnoxiousPrivacyPolicy
    obnoxiousTermsAndConditions
    organizationEntityName
    sponsorDirectoryAccessConfig
    featureFlags {
      cms
      conferences
    }
    currentUserPermissions {
      canAccessMemberDirectory
      canAccessEventsFeed
    }
    contactInfo {
      phoneNumber
      email
      addressLine0
      addressLocality
      addressAdministrativeArea
      addressPostalCode
    }
    featureFlags {
      ads
      cms
      conferences
      disableFtue
      historicalDataImport
      invoicing
      memberManagement
      memberManagementAdminOnly
      mobileGrowth
      reporting
      setReferrerPolicy
      topSessionBanner
      isMembershipV2Enabled
      isMembershipInstallmentsEnabled
      showIntegrationLogs
      showPermissionGroups
      sharedCommunityAccess
      aiTaggingDisabled
      aiAskEnabled
      webflow
      hideDateAndTimeForCourses
      websiteBuilderEmbed
      hideTradewingLogin
      attributeLibrary
      autoArchive
      contactTypes
    }
    paidModules {
      memberManagement
      websiteBuilder
    }
    logo {
      _id
      imageProxy(input: { h: 80, w: 416 })
    }
    logoIcon {
      _id
      imageProxy(input: { h: 80, w: 80, fit: "fill", fill: "solid" })
    }
    sponsorUpgradeEmails {
      userId
      email
    }
  }
`;

export type Current = {
  loading: boolean;
  user: CurrentProvider_UserFragment | null | undefined;
  tenant: CurrentProvider_TenantFragment | null | undefined;
};

const CurrentContext = React.createContext<Current | null>(null);

export const CurrentProvider: React.FC<Current> & {
  userFragment: DocumentNode;
  tenantFragment: DocumentNode;
} = ({ children, loading, user, tenant }) => {
  React.useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('accessToken');
    const refreshToken = urlParams.get('refreshToken');
    const showChatIcon = urlParams.get('showChatIcon');
    const ssoMobile = urlParams.get('ssoMobile');
    const redirectSSOUrl = urlParams.get('redirectSSOUrl');
    const ssoUserId = urlParams.get('ssoUserId');
    const tenantIdForSso = urlParams.get('tenantIdForSso');
    if (token && refreshToken) {
      try {
        // Store tokens in cookies
        const ACCESS_TOKEN_COOKIE_KEY = 'accounts:accessToken';
        const REFRESH_TOKEN_COOKIE_KEY = 'accounts:refreshToken';
        const isMobile = true;
        const showChatIcon = urlParams.get('showChatIcon');

        const options = getCookieOptions();
        document.cookie = `${ACCESS_TOKEN_COOKIE_KEY}=${token}; ${Object.entries(
          options
        )
          .map(([key, value]) => `${key}=${value}`)
          .join('; ')}`;
        document.cookie = `${REFRESH_TOKEN_COOKIE_KEY}=${refreshToken}; ${Object.entries(
          options
        )
          .map(([key, value]) => `${key}=${value}`)
          .join('; ')}`;
        document.cookie = `isMobile=${isMobile}; ${Object.entries(options)
          .map(([key, value]) => `${key}=${value}`)
          .join('; ')}`;
        document.cookie = `showChatIcon=${showChatIcon}; ${Object.entries(
          options
        )
          .map(([key, value]) => `${key}=${value}`)
          .join('; ')}`;
      } catch (error) {
        console.error('Error storing tokens:', error);
      }
    } else if (ssoMobile) {
      const options = getCookieOptions();
      document.cookie = `ssoMobile=${ssoMobile}; ${Object.entries(options)
        .map(([key, value]) => `${key}=${value}`)
        .join('; ')}`;
      document.cookie = `ssoUserId=${ssoUserId}; ${Object.entries(options)
        .map(([key, value]) => `${key}=${value}`)
        .join('; ')}`;
      document.cookie = `tenantIdForSso=${tenantIdForSso}; ${Object.entries(
        options
      )
        .map(([key, value]) => `${key}=${value}`)
        .join('; ')}`;
      if (redirectSSOUrl) {
        window.location.href = redirectSSOUrl;
      }
    }
  }, []);
  return (
    <CurrentContext.Provider value={{ loading, user, tenant }}>
      {children}
    </CurrentContext.Provider>
  );
};

CurrentProvider.userFragment = USER_FRAGMENT;
CurrentProvider.tenantFragment = TENANT_FRAGMENT;

export const useCurrent = () => {
  const context = React.useContext(CurrentContext);

  if (!context) {
    throw new Error(
      'The useCurrent() hook can only be executed inside a <CurrentProvider />'
    );
  }

  return context;
};
